import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Loader2, GraduationCap, Check, AlertCircle, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Status = "queued" | "running" | "applied" | "no_changes" | "failed";

const TERMINAL_AUTO_HIDE_MS = 6000;

const STATUS_LABELS: Record<Status, string> = {
  queued: "Queued",
  running: "Analyzing your edit…",
  applied: "Template updated",
  no_changes: "No changes needed",
  failed: "Couldn't learn from this edit",
};

function isTerminal(status: Status): boolean {
  return status === "applied" || status === "no_changes" || status === "failed";
}

export function LearnFromEditsIndicator({
  sessionId,
  sectionTitle,
  userId,
}: {
  sessionId: Id<"recordingSessions">;
  sectionTitle: string;
  userId: Id<"users">;
}) {
  const job = useQuery(api.learnFromEdits.getJobForSection, {
    sessionId,
    sectionTitle,
    userId,
  });

  // Auto-hide terminal states after a few seconds. Hidden once jobKey
  // changes, the new job will show again.
  const [hiddenFor, setHiddenFor] = useState<string | null>(null);
  const jobKey = job ? String(job._id) + ":" + job.status : null;

  useEffect(() => {
    if (!job || !isTerminal(job.status)) return;
    if (hiddenFor === jobKey) return;
    const t = setTimeout(() => setHiddenFor(jobKey), TERMINAL_AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [jobKey, job, hiddenFor]);

  if (!job) return null;
  if (isTerminal(job.status) && hiddenFor === jobKey) return null;

  const status = job.status as Status;
  const label = STATUS_LABELS[status];
  const isRunning = status === "queued" || status === "running";

  let icon;
  let tone = "";
  if (isRunning) {
    icon = <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    tone = "text-muted-foreground bg-muted/60 border-border";
  } else if (status === "applied") {
    icon = <Check className="w-3.5 h-3.5" />;
    tone =
      "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300/60";
  } else if (status === "no_changes") {
    icon = <Info className="w-3.5 h-3.5" />;
    tone = "text-muted-foreground bg-muted/60 border-border";
  } else {
    icon = <AlertCircle className="w-3.5 h-3.5" />;
    tone =
      "text-destructive bg-destructive/10 border-destructive/40";
  }

  const row = (
    <div
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${tone}`}
      data-testid={`status-learn-from-edits-${sectionTitle}`}
      data-status={status}
    >
      <GraduationCap className="w-3.5 h-3.5 shrink-0 opacity-80" />
      {icon}
      <span>{label}</span>
    </div>
  );

  if (status === "failed" && job.message) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{row}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-xs">
            {job.message}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return row;
}
